// src/pages/ViewCartPage.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowLeft } from "lucide-react";
import { LandingStyles } from "../components/landing/LandingStyles";
import { LandingHeader } from "../components/landing/LandingHeader";
import { Footer } from "../components/landing/Footer";
import { CartItemRow } from "../components/cart/CartItemRow";
import { CheckoutAuthModal } from "../components/cart/CheckoutAuthModal";
import { useCart } from "../context/CartContext";
import { getListingTotal } from "../utils/cartDisplay";
import { formatINR } from "../utils/listingFormat";

export default function ViewCartPage() {
  const { items, removeFromCart } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const listings = useMemo(() => items.map((i) => i.space), [items]);

  const subtotal = useMemo(
    () => listings.reduce((sum, l) => sum + getListingTotal(l as any), 0),
    [listings]
  );

  return (
    <div className="adroj-landing min-h-screen bg-white text-[#0F172A]">
      <LandingStyles />
      <LandingHeader />

      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0F172A]">
          <ArrowLeft className="h-4 w-4" /> Continue browsing
        </Link>

        <h1 className="font-display mt-4 text-3xl text-[#0F172A]">Your cart</h1>
        <p className="mt-1 text-sm text-slate-500">
          {listings.length} space{listings.length === 1 ? "" : "s"} selected
        </p>

        {listings.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-slate-200 py-20 text-center">
            <ShoppingCart className="h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Your cart is empty.</p>
            <Link
              to="/#listings"
              className="mt-4 rounded-full bg-[#EB4A2D] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Browse spaces
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid gap-8 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {listings.map((listing, i) => (
                <CartItemRow key={listing?.id ?? i} listing={listing as any} onRemove={removeFromCart} />
              ))}
            </div>

            <div className="h-fit rounded-2xl border border-slate-200 bg-[#F8FAFD] p-5">
              <h2 className="text-sm font-semibold text-[#0F172A]">Order summary</h2>
              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>Subtotal ({listings.length} space{listings.length === 1 ? "" : "s"})</span>
                <span>{formatINR(subtotal)}</span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm font-semibold text-[#0F172A]">Total</span>
                <span className="font-display text-2xl text-[#0F172A]">{formatINR(subtotal)}</span>
              </div>

              <button
                onClick={() => setShowAuthModal(true)}
                className="mt-5 w-full rounded-full bg-[#EB4A2D] px-4 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              >
                Checkout
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {showAuthModal && <CheckoutAuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}